import { clsx } from 'clsx'
import { useState } from 'react'
import { Popover } from './popover'
import { type TrackConfig, trackInteraction } from './tracking'

export interface PaginationProps {
  // Pagination metadata (from API) - Required
  currentPage: number
  itemsPerPage: number

  // Pagination metadata (from API) - Optional
  totalPages?: number
  totalCount?: number
  from?: number
  to?: number
  hasNextPage?: boolean
  hasPrevPage?: boolean

  // Callbacks
  onPageChange: (page: number) => void
  onItemsPerPageChange: (perPage: number) => void

  // Configuration
  itemsPerPageOptions?: number[] // Default: [20, 50, 100]
  showItemsPerPageSelector?: boolean // Default: true

  // Optional
  disabled?: boolean
  className?: string
  ariaLabel?: string
  track?: TrackConfig<{ previousPage: number; nextPage: number }>
}

export function Pagination({
  currentPage,
  totalPages: _totalPages,
  totalCount,
  from,
  to,
  hasNextPage,
  hasPrevPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [20, 50, 100],
  showItemsPerPageSelector = true,
  disabled = false,
  className,
  ariaLabel = 'Pagination',
  track,
}: PaginationProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handlePageChange = (nextPage: number) => {
    if (disabled || nextPage === currentPage) return

    onPageChange(nextPage)
    trackInteraction(track, { previousPage: currentPage, nextPage })
  }

  const handlePrevPage = () => {
    if (hasPrevPage ?? false) handlePageChange(currentPage - 1)
  }

  const handleNextPage = () => {
    if (hasNextPage ?? false) handlePageChange(currentPage + 1)
  }

  const handleItemsPerPageChange = (value: number) => {
    if (!disabled) {
      onItemsPerPageChange(value)
      setIsOpen(false)
    }
  }

  // Generate page series (similar to pagy.series)
  const generatePageSeries = (): (number | 'gap')[] => {
    const totalPages = _totalPages ?? (hasNextPage ? currentPage + 1 : currentPage)
    const series: (number | 'gap')[] = []

    // For simplicity, show current page and adjacent pages
    // This mirrors common pagination patterns
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        series.push(i)
      }
    } else {
      // Show first, last, current and adjacent pages with gaps
      if (currentPage <= 3) {
        // Near start: show 1-4, gap, last
        for (let i = 1; i <= 4; i++) {
          series.push(i)
        }
        series.push('gap')
        series.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        // Near end: show 1, gap, last 4
        series.push(1)
        series.push('gap')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          series.push(i)
        }
      } else {
        // Middle: show 1, gap, current-1 to current+1, gap, last
        series.push(1)
        series.push('gap')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          series.push(i)
        }
        series.push('gap')
        series.push(totalPages)
      }
    }

    return series
  }

  const pageSeries = generatePageSeries()

  // Button styles matching PagyNavComponent
  const buttonClasses = 'px-3 py-2 text-sm font-medium border-gray-stroke'
  const buttonHoverClasses = 'hover:bg-sand hover:text-charcoal-75'
  const buttonActiveClasses = 'bg-sand text-charcoal-75'

  return (
    <nav
      className={clsx(
        'flex flex-row items-center justify-between gap-4',
        'text-sm text-charcoal-75',
        disabled && 'opacity-40',
        className
      )}
      aria-label={ariaLabel}
    >
      {/* Left: Items per page selector */}

      {showItemsPerPageSelector && (
        <div className="flex flex-row gap-2">
          <span className="text-charcoal-50">Rows per page:</span>
          <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger className="flex items-center gap-1 text-sm text-charcoal-75 cursor-pointer">
              <span className="font-medium">{itemsPerPage}</span>
              <i className="fa-solid fa-caret-down text-xs" />
            </Popover.Trigger>
            <Popover.Content className="p-0 w-auto">
              <ul className="py-1">
                {itemsPerPageOptions.map((option) => (
                  <li key={option}>
                    <button
                      type="button"
                      className="w-full px-4 py-2 text-sm text-left hover:bg-sand cursor-pointer"
                      onClick={() => handleItemsPerPageChange(option)}
                      disabled={disabled}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            </Popover.Content>
          </Popover.Root>
        </div>
      )}

      <div className="flex items-center gap-4">
        {/* Showing X of Y */}
        <div className="flex-1 text-center hidden md:flex">
          {from !== undefined && to !== undefined && totalCount !== undefined ? (
            <span>
              Showing {from} {to > from ? `to ${to}` : ''} of {totalCount}
            </span>
          ) : totalCount !== undefined ? (
            <span>{totalCount} items</span>
          ) : null}
        </div>
        {/* Right: Navigation controls as button group */}
        <div className="inline-flex text-charcoal-50">
          {/* Previous button */}
          <button
            type="button"
            onClick={handlePrevPage}
            disabled={!(hasPrevPage ?? false) || disabled}
            aria-label="Previous page"
            className={clsx(
              buttonClasses,
              'border-t border-b border-l rounded-l-md',
              (hasPrevPage ?? false) && !disabled ? buttonHoverClasses : 'cursor-not-allowed'
            )}
          >
            <i className="fa-solid fa-arrow-left" />
          </button>

          {/* Page number buttons */}
          {pageSeries.map((item, index) => {
            const isGap = item === 'gap'
            const isCurrentPage = item === currentPage
            const isClickable = !isGap && !isCurrentPage

            return (
              <button
                key={isGap ? `gap-${index}` : item}
                type="button"
                onClick={() => !isGap && handlePageChange(item)}
                disabled={!isClickable || disabled}
                aria-label={isGap ? undefined : `Page ${item}`}
                aria-current={isCurrentPage ? 'page' : undefined}
                className={clsx(
                  buttonClasses,
                  'border-t border-b border-r',
                  index === 0 && 'border-l',
                  isCurrentPage && buttonActiveClasses,
                  isClickable && !disabled ? buttonHoverClasses : 'cursor-default'
                )}
              >
                {isGap ? '…' : item}
              </button>
            )
          })}

          {/* Next button */}
          <button
            type="button"
            onClick={handleNextPage}
            disabled={!(hasNextPage ?? false) || disabled}
            aria-label="Next page"
            className={clsx(
              buttonClasses,
              'border-t border-b border-r rounded-r-md',
              (hasNextPage ?? false) && !disabled ? buttonHoverClasses : 'cursor-not-allowed'
            )}
          >
            <i className="fa-solid fa-arrow-right" />
          </button>
        </div>
      </div>
    </nav>
  )
}
